import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import {
  ArrowLeft,
  AlertCircle,
  Download,
  Printer,
  Mail,
  CreditCard,
  Clock,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Eye,
  MoreVertical,
  Trash2,
  User,
  MapPin,
  Calendar,
  DollarSign,
  Settings,
  Bell,
  RotateCcw,
  CircleDot,
  Building,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface InvoiceLineItem {
  description: string;
  qty: number;
  unitPrice: number;
  amount: number;
  category?: string;
}

interface PaymentRecord {
  date: string;
  amount: string;
  method: string;
  reference: string;
  status: string;
}

interface AuditLog {
  action: string;
  timestamp: string;
  actor: string;
  details?: string;
}

const OverdueInvoiceDetailPage = () => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Sample invoice data
  const invoice = {
    id: 'INV-2024-0082',
    issueDate: 'Oct 12, 2023',
    dueDate: 'Nov 12, 2023',
    status: 'OVERDUE',
    daysOverdue: 32,
  };

  const customer = {
    name: 'Elena Vance',
    email: 'elena.vance@lumon.corp',
    accountType: 'Enterprise Account - EV-0091',
    creditLimit: '£50,000.00',
  };

  const serviceProperty = {
    name: 'Lumon Industries HQ',
    address: '100 Kier Drive, Floor 13',
    location: 'Wilmington, DE 19801',
  };

  const lineItems: InvoiceLineItem[] = [
    {
      description: 'Annual Enterprise Platform Subscription',
      qty: 1,
      unitPrice: 6500,
      amount: 6500,
      category: 'Tier 3 Compliance Pack + Unlimited Seats',
    },
    {
      description: 'On-site Security Audit & Compliance QA',
      qty: 3,
      unitPrice: 500,
      amount: 1500,
      category: 'Scheduled Oct 15 - Oct 17',
    },
    {
      description: 'Cloud Data Recovery (Add-on)',
      qty: 1,
      unitPrice: 450,
      amount: 450,
      category: 'High-priority backup stream integration',
    },
  ];

  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const vat = subtotal * 0.2;
  const totalAmount = subtotal + vat;
  const paidAmount = 0;
  const balanceDue = totalAmount - paidAmount;

  const accountStanding = {
    status: 'At Risk',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    overdueInvoices: 2,
    totalOutstanding: balanceDue,
    creditLimit: 50000,
  };

  const paymentHistory: PaymentRecord[] = [
    {
      date: 'Oct 12, 10:45 AM',
      amount: '-',
      method: '-',
      reference: '-',
      status: 'No payments have been recorded for this invoice.',
    },
  ];

  const auditTrail: AuditLog[] = [
    {
      action: 'System generated invoice INV-2024-0082',
      timestamp: 'Oct 12, 10:45 AM',
      actor: 'System',
    },
    {
      action: 'Elena Vance viewed the invoice via portal',
      timestamp: 'Oct 13, 02:12 PM',
      actor: 'Elena Vance',
    },
    {
      action: 'Invoice marked as Overdue by Automated Billing Task',
      timestamp: 'Nov 13, 12:00 AM',
      actor: 'Automated Billing Task',
      details: 'marked as Overdue',
    },
  ];

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    // Simulate API call
    setTimeout(() => {
      setSavingNotes(false);
      alert('Notes saved successfully');
    }, 1000);
  };

  return (
    <>
      <Helmet>
        <title>Invoice {invoice.id} - Overdue - VireWire Admin</title>
        <meta name="description" content="Manage overdue invoice" />
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen flex flex-col">
          {/* Top Navigation Bar */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex gap-8">
                  <button className="text-xs font-bold text-gray-600 uppercase tracking-widest hover:text-teal-600 pb-4 pt-4 border-b-2 border-transparent">
                    FINANCE
                  </button>
                  <button className="text-xs font-bold text-teal-600 uppercase tracking-widest pb-4 pt-4 border-b-2 border-teal-600">
                    BILLING
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <CircleDot className="w-3 h-3 text-green-500 fill-green-500" />
                    <span className="text-xs font-bold text-green-600 uppercase tracking-widest">OPERATIONAL</span>
                  </div>
                  <input type="text" placeholder="Search..." className="w-40 px-3 py-2 border border-gray-300 rounded text-sm" />
                  <Bell className="w-5 h-5 text-gray-600 cursor-pointer" />
                  <RotateCcw className="w-5 h-5 text-gray-600 cursor-pointer" />
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm cursor-pointer">
                    A
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600">Admin User</p>
                    <p className="text-xs text-gray-500">SYSTEM ADMINISTRATOR</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-8">
            <div className="max-w-7xl mx-auto">
              {/* Header Section */}
              <div className="mb-8 pb-6 border-b border-gray-200 bg-white rounded-lg p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900">
                      INVOICE #{invoice.id}
                    </h1>
                    <div className="flex items-center gap-3 mt-3">
                      <Badge className="bg-red-600 text-white font-bold">● OVERDUE</Badge>
                      <button className="text-sm text-gray-600 hover:text-gray-900 font-bold">
                        ← BACK TO LIST
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-600 uppercase tracking-widest">MANAGE INVOICE</p>
                  </div>
                </div>

                {/* Top Info Row */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Issue Date</p>
                    <p className="text-lg font-bold text-gray-900 mt-2">{invoice.issueDate}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-red-600 uppercase tracking-widest">Due Date</p>
                    <p className="text-lg font-bold text-red-600 mt-2">{invoice.dueDate}</p>
                    <p className="text-xs text-red-600 mt-1">{invoice.daysOverdue} days overdue</p>
                  </div>
                  <div className="col-span-2 flex items-center">
                    <div className="w-full">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Total Amount</p>
                      <div className="mt-2 bg-teal-600 rounded-lg p-4 inline-block w-full">
                        <p className="text-4xl font-bold text-white">
                          £{totalAmount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content Section */}
              <div className="grid grid-cols-3 gap-6">
                {/* Sidebar - Right Side (Actions First) */}
                <div className="order-last space-y-4">
                  {/* Manage Invoice Actions */}
                  <Card className="bg-white rounded-lg border border-gray-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        MANAGE INVOICE
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        onClick={() => setShowPaymentModal(true)}
                        className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 rounded-lg"
                      >
                        <CreditCard className="w-4 h-4" />
                        Record Manual Payment
                      </Button>
                      <Button
                        onClick={() => setShowReminderModal(true)}
                        className="w-full flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 rounded-lg"
                      >
                        <Mail className="w-4 h-4" />
                        Send Reminder to Customer
                      </Button>
                      
                      <div className="pt-2 flex gap-2">
                        <Button
                          className="flex-1 flex items-center justify-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-600 font-bold border border-blue-200 py-2 rounded-lg text-xs"
                          variant="outline"
                        >
                          <Download className="w-4 h-4" />
                          PDF
                        </Button>
                        <Button
                          className="flex-1 flex items-center justify-center gap-1 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold border border-gray-200 py-2 rounded-lg text-xs"
                          variant="outline"
                        >
                          <Printer className="w-4 h-4" />
                          PRINT
                        </Button>
                      </div>

                      <Button
                        className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 font-bold border border-red-200 py-2 rounded-lg text-sm"
                        variant="outline"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        Void Invoice
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Account Standing */}
                  <Card className="bg-slate-700 text-white border-0 rounded-lg">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-bold text-gray-300 uppercase tracking-widest">
                        ACCOUNT STANDING
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-3xl font-bold text-white">
                          {accountStanding.status}
                        </p>
                      </div>

                      <div className="space-y-3 border-t border-gray-600 pt-4">
                        <div>
                          <p className="text-xs font-bold text-gray-400 uppercase">
                            Overdue Invoices
                          </p>
                          <p className="text-2xl font-bold text-white mt-1">
                            {accountStanding.overdueInvoices}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs font-bold text-gray-400 uppercase">
                            Total Outstanding
                          </p>
                          <p className="text-2xl font-bold text-white mt-1">
                            £{accountStanding.totalOutstanding.toFixed(2)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs font-bold text-gray-400 uppercase">
                            Credit Limit
                          </p>
                          <p className="text-2xl font-bold text-white mt-1">
                            £{accountStanding.creditLimit.toFixed(2)}
                          </p>
                        </div>

                        <Button className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold mt-4 py-2 rounded-lg text-sm">
                          View Customer Profile
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Internal Notes */}
                  <Card className="bg-white rounded-lg border border-gray-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        INTERNAL NOTES
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <textarea
                        value={internalNotes}
                        onChange={(e) => setInternalNotes(e.target.value)}
                        placeholder="Add a private note for other admins..."
                        className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        rows={4}
                      />
                      <Button
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 rounded-lg"
                      >
                        {savingNotes ? 'Saving...' : 'SAVE NOTE'}
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Left Content - 2 columns */}
                <div className="col-span-2 space-y-4">
                  {/* Customer Details */}
                  <Card className="bg-white rounded-lg border border-gray-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        CUSTOMER DETAILS
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                          EV
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-lg">{customer.name}</p>
                          <p className="text-sm text-gray-600">{customer.email}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {customer.accountType}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Service Property */}
                  <Card className="bg-white rounded-lg border border-gray-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        SERVICE PROPERTY
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-teal-100 rounded flex items-center justify-center flex-shrink-0 mt-1">
                          <Building className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-lg">{serviceProperty.name}</p>
                          <p className="text-sm text-gray-600 mt-1">{serviceProperty.address}</p>
                          <p className="text-sm text-gray-600">{serviceProperty.location}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Invoice Items */}
                  <Card className="bg-white rounded-lg border border-gray-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        DESCRIPTION
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {lineItems.map((item, index) => (
                          <div key={index} className="pb-4 border-b border-gray-200 last:border-0">
                            <div className="flex justify-between gap-4 mb-2">
                              <div>
                                <p className="font-bold text-gray-900">{item.description}</p>
                                {item.category && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {item.category}
                                  </p>
                                )}
                              </div>
                              <p className="font-bold text-gray-900 whitespace-nowrap">
                                £{item.amount.toFixed(2)}
                              </p>
                            </div>
                            <div className="flex justify-between text-sm text-gray-600">
                              <span className="uppercase text-xs font-semibold">QTY</span>
                              <span>
                                <span className="uppercase text-xs font-semibold">UNIT PRICE</span>
                              </span>
                              <span>
                                <span className="uppercase text-xs font-semibold">AMOUNT</span>
                              </span>
                            </div>
                            <div className="flex justify-between text-sm text-gray-600 mt-1">
                              <span>{item.qty}</span>
                              <span>£{parseFloat(item.unitPrice).toFixed(2)}</span>
                              <span>£{parseFloat(item.amount).toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Totals */}
                      <div className="mt-6 space-y-2 pt-4 border-t border-gray-300">
                        <div className="flex justify-between">
                          <span className="uppercase text-sm font-bold text-gray-600">SUBTOTAL</span>
                          <span className="font-bold text-gray-900">
                            £{subtotal.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="uppercase text-sm font-bold text-gray-600">VAT (20%)</span>
                          <span className="font-bold text-gray-900">
                            £{vat.toFixed(2)}
                          </span>
                        </div>

                        <div className="flex justify-between pt-2 border-t border-gray-300 mt-2">
                          <span className="text-lg font-bold text-gray-900">
                            TOTAL AMOUNT
                          </span>
                          <span className="text-xl font-bold text-gray-900">
                            £{totalAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payment History */}
                  <Card className="bg-white rounded-lg border border-gray-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        PAYMENT HISTORY
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-8">
                        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">
                          No payments have been recorded for this invoice.
                        </p>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm font-bold text-gray-600 uppercase">BALANCE DUE:</p>
                        <p className="text-2xl font-bold text-red-600 mt-2">£{balanceDue.toFixed(2)}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Audit Trail */}
                  <Card className="bg-white rounded-lg border border-gray-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                        AUDIT TRAIL
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {auditTrail.map((log, index) => (
                          <div key={index} className="flex gap-4 pb-4 border-b border-gray-200 last:border-0">
                            <div className="flex-shrink-0 mt-1">
                              {index === 0 && (
                                <div className="w-2 h-2 rounded-full bg-gray-400 mt-1" />
                              )}
                              {index === 1 && (
                                <div className="w-2 h-2 rounded-full bg-gray-300 mt-1" />
                              )}
                              {index === 2 && (
                                <div className="w-2 h-2 rounded-full bg-red-600 mt-1" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-gray-900">
                                <span className="font-bold">{log.actor}</span> {log.action}
                              </p>
                              {log.details && (
                                <p className="text-xs text-gray-500 mt-1 font-bold">
                                  {log.details}
                                </p>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 text-right flex-shrink-0 whitespace-nowrap">
                              {log.timestamp}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Modal */}
          {showPaymentModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <Card className="w-full max-w-md">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <CardTitle>Record Manual Payment</CardTitle>
                    <button
                      onClick={() => setShowPaymentModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-bold text-gray-600">
                        Payment Amount
                      </label>
                      <input
                        type="number"
                        placeholder="£0.00"
                        className="w-full mt-2 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-600">
                        Payment Method
                      </label>
                      <select className="w-full mt-2 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                        <option>Bank Transfer</option>
                        <option>Credit Card</option>
                        <option>Cheque</option>
                        <option>Cash</option>
                      </select>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowPaymentModal(false)}
                      >
                        Cancel
                      </Button>
                      <Button className="flex-1 bg-teal-600 hover:bg-teal-700 text-white">
                        Record Payment
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Reminder Modal */}
            {showReminderModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <Card className="w-full max-w-md">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <CardTitle>Send Payment Reminder</CardTitle>
                    <button
                      onClick={() => setShowReminderModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-bold text-gray-600">
                        Send to
                      </label>
                      <input
                        type="email"
                        defaultValue={customer.email}
                        className="w-full mt-2 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-600">
                        Message
                      </label>
                      <textarea
                        placeholder="Enter custom reminder message..."
                        className="w-full mt-2 px-4 py-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowReminderModal(false)}
                      >
                        Cancel
                      </Button>
                      <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">
                        Send Reminder
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
        </div>
      </AdminLayout>
    </>
  );
};

export default OverdueInvoiceDetailPage;
